/**
 * Validates Wolt settlement Excel files.
 *
 * Required columns: "Venue Name", "Total Goods Sold".
 * Parses revenue (net/vat/gross), all fee buckets (net/vat/gross),
 * payout amount, and dates for the upload logic.
 */
import * as XLSX from 'xlsx';

const VENUE_COLUMN_NAMES = ['venue name', 'venue'];

/**
 * Fee bucket definitions matching the old Wolt edge function.
 * Each bucket has net, vat, gross column names (lowercase for matching).
 */
export const FEE_BUCKET_DEFS = [
  { key: 'commission', net: 'total net commission amount', vat: 'total vat commission amount', gross: 'total gross commission amount' },
  { key: 'addition', net: 'addition net amount', vat: 'addition vat amount', gross: 'addition gross amount' },
  { key: 'penalties', net: 'additional commission fees penalties net amount', vat: 'additional commission fees penalties vat amount', gross: 'additional commission fees penalties gross amount' },
  { key: 'deduction', net: 'deduction net amount', vat: 'deduction vat amount', gross: 'deduction gross amount' },
  { key: 'productSales', net: 'product sales net amount', vat: 'product sales vat amount', gross: 'product sales gross amount' },
  { key: 'campaignDiscount', net: 'total venue campaign discount net amount', vat: 'total venue campaign discount vat amount', gross: 'total venue campaign discount gross amount' },
  { key: 'saleCorrection', net: 'charging sale correction net amount', vat: 'charging sale correction vat amount', gross: 'charging sale correction gross amount' },
  { key: 'campaignCommission', net: 'venue campaign discount commission net amount', vat: 'venue campaign discount commission vat amount', gross: 'venue campaign discount commission gross amount' },
];

/** Columns shown in the UI pivot table */
export const WOLT_TYPES = ['totalGoodsSold', 'totalGrossCommission', 'totalPayoutAmount'];

export const WOLT_TYPE_LABELS = {
  totalGoodsSold: 'Total Goods Sold',
  totalGrossCommission: 'Total Gross Commission',
  totalPayoutAmount: 'Total Payout Amount',
};

function normalizeHeader(str) {
  if (str == null || typeof str !== 'string') return '';
  return str.toString().trim().toLowerCase();
}

function findColumnIndex(headers, possibleNames) {
  const normalized = headers.map(normalizeHeader);
  for (const name of possibleNames) {
    const idx = normalized.indexOf(name.toLowerCase());
    if (idx !== -1) return idx;
  }
  return -1;
}

function findColumnIndexSingle(headers, name) {
  return findColumnIndex(headers, [name]);
}

function sheetToRows(sheet) {
  const cellKeys = Object.keys(sheet).filter((k) => !k.startsWith('!'));
  if (cellKeys.length === 0) return [];

  let maxRow = 0;
  let maxCol = 0;
  cellKeys.forEach((key) => {
    const ref = XLSX.utils.decode_cell(key);
    maxRow = Math.max(maxRow, ref.r);
    maxCol = Math.max(maxCol, ref.c);
  });

  const rows = [];
  for (let r = 0; r <= maxRow; r++) {
    const row = [];
    for (let c = 0; c <= maxCol; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[addr];
      let value = '';
      if (cell) {
        value = cell.w !== undefined ? cell.w : (cell.v !== undefined ? cell.v : '');
      }
      row.push(value);
    }
    rows.push(row);
  }
  return rows;
}

export async function parseWoltSettlementFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array', raw: false });
  if (!workbook.SheetNames?.length) {
    throw new Error('File has no worksheets');
  }
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) {
    throw new Error('Could not read the first sheet');
  }

  let raw = sheetToRows(sheet);
  if (raw.length === 0) {
    raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
  }
  if (!raw.length) {
    throw new Error('Sheet is empty');
  }

  const headers = raw[0].map((c) => (c != null ? String(c).trim() : ''));
  const rows = raw.slice(1);
  return { headers, rows };
}

function parseAmount(raw) {
  if (raw === '' || raw == null) return 0;
  const str = String(raw).trim();
  if (!str) return 0;
  let cleaned = str.replace(/\s/g, '');
  const lastComma = cleaned.lastIndexOf(',');
  const lastPeriod = cleaned.lastIndexOf('.');
  if (lastComma > lastPeriod) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastPeriod > lastComma) {
    cleaned = cleaned.replace(/,/g, '');
  }
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

function parseDate(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function cellStr(row, idx) {
  if (idx < 0 || idx >= row.length) return '';
  return row[idx] != null ? String(row[idx]).trim() : '';
}

export function validateWoltSettlementData(headers, rows) {
  const errors = [];
  const warnings = [];
  const columnIndices = {};
  const firstRowNum = 2;

  // Required: Venue Name
  const venueIdx = findColumnIndex(headers, VENUE_COLUMN_NAMES);
  if (venueIdx === -1) {
    errors.push(
      `Missing required column: "Venue Name". Found columns: ${headers.filter(Boolean).join(', ') || '(none)'}`
    );
  }
  columnIndices.venueName = venueIdx;

  // Required: Total Goods Sold (revenue gross)
  const revenueGrossIdx = findColumnIndexSingle(headers, 'total goods sold');
  if (revenueGrossIdx === -1) {
    errors.push(
      `Missing required column: "Total Goods Sold". Found columns: ${headers.filter(Boolean).join(', ') || '(none)'}`
    );
  }
  columnIndices.revenueGross = revenueGrossIdx;

  // Revenue net/vat (for VAT rate inference)
  columnIndices.revenueNet = findColumnIndexSingle(headers, 'total net goods sold');
  columnIndices.revenueVat = findColumnIndexSingle(headers, 'total vat goods sold');

  // Fee bucket columns (all optional, default 0 if missing)
  const feeBucketIndices = FEE_BUCKET_DEFS.map((def) => ({
    key: def.key,
    netIdx: findColumnIndexSingle(headers, def.net),
    vatIdx: findColumnIndexSingle(headers, def.vat),
    grossIdx: findColumnIndexSingle(headers, def.gross),
  }));

  // Summary columns for UI display
  columnIndices.totalGrossCommission = findColumnIndexSingle(headers, 'total gross commission amount');
  columnIndices.totalPayoutAmount = findColumnIndexSingle(headers, 'total payout amount');

  // Date columns
  columnIndices.timeFrameStartDate = findColumnIndexSingle(headers, 'time frame start date');
  columnIndices.timeFrameEndDate = findColumnIndexSingle(headers, 'time frame end date');
  columnIndices.payoutIssueDate = findColumnIndexSingle(headers, 'payout issue date');

  if (errors.length > 0) {
    return { isValid: false, errors, warnings, data: [], columnIndices };
  }

  const data = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = firstRowNum + i;
    if (!Array.isArray(row)) {
      warnings.push(`Row ${rowNum}: skipped (not a row)`);
      continue;
    }

    const venueName = cellStr(row, columnIndices.venueName);
    const revenueGross = parseAmount(cellStr(row, columnIndices.revenueGross));
    const commissionGross = parseAmount(cellStr(row, columnIndices.totalGrossCommission));
    const payoutAmount = parseAmount(cellStr(row, columnIndices.totalPayoutAmount));

    if (!venueName && revenueGross === 0 && commissionGross === 0 && payoutAmount === 0) {
      continue;
    }

    if (!venueName) {
      errors.push(`Row ${rowNum}: "Venue Name" is required`);
    }

    // Revenue net/vat for VAT rate inference
    const revenueNet = parseAmount(cellStr(row, columnIndices.revenueNet));
    const revenueVat = parseAmount(cellStr(row, columnIndices.revenueVat));

    // Parse all fee buckets
    const feeBuckets = feeBucketIndices.map((fb) => ({
      key: fb.key,
      net: parseAmount(cellStr(row, fb.netIdx)),
      vat: parseAmount(cellStr(row, fb.vatIdx)),
      gross: parseAmount(cellStr(row, fb.grossIdx)),
    }));

    data.push({
      venueName: venueName || '',
      // Revenue detail
      revenueNet,
      revenueVat,
      revenueGross,
      // Fee buckets
      feeBuckets,
      // Summary fields for UI pivot
      totalGoodsSold: revenueGross,
      totalGrossCommission: commissionGross,
      totalPayoutAmount: payoutAmount,
      // Dates
      timeFrameStartDate: parseDate(cellStr(row, columnIndices.timeFrameStartDate)),
      timeFrameEndDate: parseDate(cellStr(row, columnIndices.timeFrameEndDate)),
      payoutIssueDate: parseDate(cellStr(row, columnIndices.payoutIssueDate)),
      rowIndex: rowNum,
    });
  }

  if (data.length === 0 && rows.length > 0) {
    warnings.push('No valid data rows found.');
  }
  if (errors.length > 0) {
    return { isValid: false, errors, warnings, data: [], columnIndices };
  }

  return { isValid: true, errors: [], warnings, data, columnIndices };
}

export async function validateWoltSettlementFile(file) {
  const { headers, rows } = await parseWoltSettlementFile(file);
  return validateWoltSettlementData(headers, rows);
}

/**
 * Group data by Venue Name, summing the three summary columns for UI display.
 */
export function groupByVenue(data) {
  const venueSet = new Set();
  const matrix = new Map();

  for (const d of data) {
    const venue = d.venueName || '';
    if (!venue) continue;
    venueSet.add(venue);
    if (!matrix.has(venue)) matrix.set(venue, new Map());
    const row = matrix.get(venue);
    for (const type of WOLT_TYPES) {
      row.set(type, (row.get(type) || 0) + (d[type] ?? 0));
    }
  }

  const rows = Array.from(venueSet).sort((a, b) => a.localeCompare(b));
  const columns = WOLT_TYPES;

  const colTotals = new Map();
  for (const venue of rows) {
    for (const type of columns) {
      const v = matrix.get(venue)?.get(type) ?? 0;
      colTotals.set(type, (colTotals.get(type) || 0) + v);
    }
  }

  return { rows, columns, matrix, colTotals };
}
